import { Disclosure, SearchBar, type Column } from "@legal-500/legal500-design-system";
import { useMemo, useState } from "react";
import { DataTable } from "@/components/DataTable/DataTable";
import { PageHeader } from "@/components/PageHeader";
import { UnauthorizedView } from "@/components/UnauthorizedView";
import { usePermissions } from "@/hooks/usePermissions";
import {
  EDITORIAL_CATEGORIES,
  isNumericId,
  SUBSCRIPTION_REGIONS,
  searchJurisdictions,
  type EditorialCategory,
  type Jurisdiction,
  type SearchMatch,
  type SubscriptionRegion,
} from "./mapping";
import styles from "./RegionMapping.module.css";

function label(jurisdiction: Jurisdiction): string {
  return `${jurisdiction.name} (${jurisdiction.id})`;
}

function JurisdictionList({ jurisdictions }: { jurisdictions: Jurisdiction[] }) {
  return (
    <ul className={styles.plainList}>
      {jurisdictions.map(jurisdiction => (
        <li key={jurisdiction.id}>{label(jurisdiction)}</li>
      ))}
    </ul>
  );
}

function UnlockedBy({
  category,
}: {
  category: Pick<EditorialCategory, "unlockedByAny" | "unlockedBy">;
}) {
  if (category.unlockedByAny) return <>Any subscription with the Marketing Toolkit</>;
  if (category.unlockedBy.length === 0) return <None />;
  return <JurisdictionList jurisdictions={category.unlockedBy} />;
}

function None() {
  return <span className={styles.muted}>—</span>;
}

const REGION_COLUMNS: Column<SubscriptionRegion>[] = [
  {
    header: "Scope",
    accessor: "scopeId",
    render: (_value, row) => (
      <>
        <span className={styles.code}>{row.scopeId}</span>
        {row.computed && <span className={styles.muted}> computed</span>}
      </>
    ),
  },
  { header: "Name", accessor: "name" },
  {
    header: "Jurisdictions",
    accessor: "jurisdictions",
    render: (_value, row) => (
      <Disclosure summary={String(row.jurisdictions.length)} className={styles.cellDisclosure}>
        <JurisdictionList jurisdictions={row.jurisdictions} />
      </Disclosure>
    ),
  },
];

const CATEGORY_COLUMNS: Column<EditorialCategory>[] = [
  {
    header: "Category",
    accessor: "name",
    render: (_value, row) => label(row),
  },
  {
    header: "Unlocked by",
    accessor: "unlockedBy",
    render: (_value, row) => <UnlockedBy category={row} />,
  },
];

const MATCH_COLUMNS: Column<SearchMatch>[] = [
  {
    header: "Jurisdiction",
    accessor: "name",
    render: (_value, row) => label(row),
  },
  {
    header: "Opened by",
    accessor: "openedBy",
    render: (_value, row) => (
      <>
        {row.openedBy}
        {!row.inAnyRegion && <p className={styles.muted}>No region includes it.</p>}
      </>
    ),
  },
  {
    header: "Unlocks category",
    accessor: "categories",
    render: (_value, row) =>
      row.categories.length === 0 ? <None /> : <JurisdictionList jurisdictions={row.categories} />,
  },
  {
    header: "Unlocked by",
    accessor: "unlockedBy",
    render: (_value, row) => <UnlockedBy category={row} />,
  },
];

function SearchResults({ query }: { query: string }) {
  const matches = useMemo(() => searchJurisdictions(query), [query]);
  const term = query.trim();
  const emptyMessage = isNumericId(term)
    ? `${term} is not a recognised jurisdiction id. A subscription set to it opens nothing.`
    : `No jurisdiction matches "${term}".`;

  return (
    <section aria-label="Search results">
      <h2 className={styles.sectionTitle}>Search results</h2>
      <DataTable data={matches} columns={MATCH_COLUMNS} rowKey="id" emptyMessage={emptyMessage} />
    </section>
  );
}

export function RegionMappingPage() {
  const { hasPermission } = usePermissions();
  const canRead = hasPermission("staff.marketing.diagnostics");
  const [query, setQuery] = useState("");

  if (!canRead) {
    return <UnauthorizedView />;
  }

  return (
    <div className={styles.wrapper}>
      <PageHeader
        title="Region mapping"
        subtitle="Which jurisdictions a Marketing Toolkit subscription opens."
      />

      <p className={styles.muted}>This is the mapping built into the release you are using.</p>

      <div className={styles.searchSection}>
        <SearchBar
          value={query}
          onChange={setQuery}
          onClear={() => setQuery("")}
          placeholder="Search by jurisdiction name or id"
        />
      </div>

      <div className={styles.contentSection}>
        {query.trim() !== "" && <SearchResults query={query} />}

        <section aria-label="Subscription regions">
          <h2 className={styles.sectionTitle}>Subscription regions</h2>
          <p className={styles.sectionDescription}>
            Only complete, in-date subscriptions whose package includes the Marketing Toolkit count.
            A subscription's Country / Region set to one of these scopes opens every jurisdiction
            listed against it. Set to a single jurisdiction id, it opens that jurisdiction, and the
            ids in its placement countries open theirs; an id opens something only if Global lists
            it, and any other value opens nothing. Global is every jurisdiction any region lists. A
            firm whose subscriptions open all of them also sees rows with a Worldwide or empty
            jurisdiction. Premium rows follow the same mapping, through subscriptions whose package
            includes Marketing Toolkit Premium. Bar sets and stables are the exception: once they
            hold any jurisdiction, they see all their rows, and with any Premium jurisdiction every
            row is Premium.
          </p>
          <DataTable data={SUBSCRIPTION_REGIONS} columns={REGION_COLUMNS} rowKey="scopeId" />
        </section>

        <section aria-label="Editorial categories">
          <h2 className={styles.sectionTitle}>Editorial categories</h2>
          <p className={styles.sectionDescription}>
            A firm whose subscriptions open any jurisdiction under Unlocked by also sees the rows
            filed under the category. A category unlocked by any subscription opens for every firm
            whose subscriptions open at least one jurisdiction. A subscription set to the category
            itself opens only the category, not its members.
          </p>
          <DataTable data={EDITORIAL_CATEGORIES} columns={CATEGORY_COLUMNS} rowKey="id" />
        </section>
      </div>
    </div>
  );
}

export default RegionMappingPage;
